    import React, { useEffect, useState } from "react";
    import { api } from "../../api/client";

    // Helper to format event type names
    const formatEventType = (type) => {
        if (!type) return '—';
        return type
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    export default function AdminLogs() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedLog, setSelectedLog] = useState(null);
    const logsPerPage = 20;

    useEffect(() => {
        fetchLogs();
    }, []);

    const fetchLogs = async () => {
        setLoading(true);
        try {
        const res = await api.get("/api/admin/logs?limit=100");
        setLogs(res.data);
        } catch (err) {
        console.error('Failed to fetch logs:', err);
        alert('Failed to load webhook logs');
        } finally {
        setLoading(false);
        }
    };

    // Calculate pagination
    const indexOfLastLog = currentPage * logsPerPage;
    const indexOfFirstLog = indexOfLastLog - logsPerPage;
    const currentLogs = logs.slice(indexOfFirstLog, indexOfLastLog);
    const totalPages = Math.ceil(logs.length / logsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const getStatusColor = (statusCode) => {
        if (statusCode >= 200 && statusCode < 300) return 'text-green-600';
        if (statusCode >= 400 && statusCode < 500) return 'text-yellow-600';
        if (statusCode >= 500) return 'text-red-600';
        return 'text-gray-600';
    };

    const getStatusBadge = (statusCode) => {
        if (statusCode === 0) return 'bg-gray-100 text-gray-800';
        if (statusCode >= 200 && statusCode < 300) return 'bg-green-100 text-green-800';
        if (statusCode >= 400 && statusCode < 500) return 'bg-yellow-100 text-yellow-800';
        if (statusCode >= 500) return 'bg-red-100 text-red-800';
        return 'bg-gray-100 text-gray-800';
    };

    const viewLogDetails = (log) => {
        setSelectedLog(log);
    };

    const replayWebhook = async (logId) => {
        if (!window.confirm('Replay this webhook?')) return;
        try {
        await api.post(`/api/admin/logs/${logId}/replay`);
        alert('Webhook replayed successfully!');
        await fetchLogs();
        } catch (err) {
        console.error('Failed to replay webhook:', err);
        alert('Failed to replay the webhook');
        }
    };

    if (loading) {
        return (
        <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
        );
    }

    return (
        <div className="space-y-6">
        <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold">Webhook Logs</h1>
            <button
            onClick={fetchLogs}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
            Refresh
            </button>
        </div>

        {logs.length === 0 ? (
            <div className="bg-white p-8 rounded-lg shadow text-center">
            <p className="text-gray-600">No webhook logs found.</p>
            </div>
        ) : (
            <>
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-100">
                    <tr>
                        <th className="p-3 text-left">ID</th>
                        <th className="p-3 text-left">Event Type</th>
                        <th className="p-3 text-left">Target URL</th>
                        <th className="p-3 text-left">Status</th>
                        <th className="p-3 text-left">Response Time</th>
                        <th className="p-3 text-left">Timestamp</th>
                        <th className="p-3 text-right">Actions</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                    {currentLogs.map(log => (
                        <tr key={log.id} className="hover:bg-gray-50">
                        <td className="p-3 text-sm">{log.id}</td>
                        <td className="p-3">
                            <span className="font-medium text-sm">{formatEventType(log.event_type)}</span>
                        </td>
                        <td className="p-3">
                            <a 
                            href={log.target_url} 
                            target="_blank" 
                            rel="noreferrer"
                            className="text-blue-600 hover:underline text-sm truncate block max-w-xs"
                            title={log.target_url}
                            >
                            {log.target_url}
                            </a>
                        </td>
                        <td className="p-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(log.status_code)}`}>
                            {log.status_code || 'Failed'}
                            </span>
                        </td>
                        <td className="p-3 text-sm">
                            {log.response_time_ms ? `${log.response_time_ms}ms` : '—'}
                        </td>
                        <td className="p-3 text-sm text-gray-600">
                            {new Date(log.created_at).toLocaleString()}
                        </td>
                        <td className="p-3 text-right space-x-2">
                            <button
                            onClick={() => viewLogDetails(log)}
                            className="text-blue-600 hover:underline text-sm"
                            >
                            View
                            </button>
                            {log.status_code >= 400 && (
                            <button
                                onClick={() => replayWebhook(log.id)}
                                className="text-green-600 hover:underline text-sm"
                            >
                                Replay
                            </button>
                            )}
                        </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
                </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="bg-white p-4 rounded-lg shadow flex justify-center items-center space-x-2">
                <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Previous
                </button>
                
                <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                </span>
                
                <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Next
                </button>
                </div>
            )}
            </>
        )}

        {/* Log Details Modal */}
        {selectedLog && (
            <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
            <div className="bg-white w-full max-w-4xl p-6 rounded shadow relative max-h-[80vh] overflow-y-auto">
                <button
                onClick={() => setSelectedLog(null)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl"
                >
                &times;
                </button>

                <h2 className="text-2xl font-bold mb-4">Webhook Log Details</h2>

                <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                    <strong>Log ID:</strong> {selectedLog.id}
                    </div>
                    <div>
                    <strong>Event Type:</strong> {selectedLog.event_type}
                    </div>
                    <div>
                    <strong>Status Code:</strong>{' '}
                    <span className={getStatusColor(selectedLog.status_code)}>
                        {selectedLog.status_code || 'Failed'}
                    </span>
                    </div>
                    <div>
                    <strong>Response Time:</strong> {selectedLog.response_time_ms ? `${selectedLog.response_time_ms}ms` : '—'}
                    </div>
                    <div>
                    <strong>Retry Count:</strong> {selectedLog.retry_count || 0}
                    </div>
                    <div>
                    <strong>Timestamp:</strong> {new Date(selectedLog.created_at).toLocaleString()}
                    </div>
                </div>

                <div>
                    <strong>Target URL:</strong>
                    <p className="text-blue-600 break-all">{selectedLog.target_url}</p>
                </div>

                {selectedLog.delivery_id && (
                    <div>
                    <strong>Delivery ID:</strong>
                    <p className="font-mono text-sm">{selectedLog.delivery_id}</p>
                    </div>
                )}

                <div>
                    <strong>Response:</strong>
                    <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
                    {selectedLog.response || 'No response data'}
                    </pre>
                </div>
                </div>

                <div className="flex gap-4 justify-end mt-6">
                {selectedLog.status_code >= 400 && (
                    <button
                    onClick={() => {
                        replayWebhook(selectedLog.id);
                        setSelectedLog(null);
                    }}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                    >
                    Replay Webhook
                    </button>
                )}
                <button
                    onClick={() => setSelectedLog(null)}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                >
                    Close
                </button>
                </div>
            </div>
            </div>
        )}
        </div>
    );
    }